import { PenTool, Compass, Rocket, RefreshCw } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHeading, useDocTitle } from "../components/Reveal";
import { MediaImg } from "../components/MediaImg";
import { CTABanner } from "../components/CTABanner";
import { GoldLink, GhostLink } from "../components/Buttons";
import { BrandFlow } from "../components/BrandFlow";
import { IMAGES } from "../data/site";

const PILLARS = [
  {
    icon: PenTool,
    title: "Brand Identity",
    copy: "Logo creation, colors, typography and visual systems — the complete look, built as a kit your whole team can actually use.",
  },
  {
    icon: Compass,
    title: "Brand Strategy",
    copy: "Positioning, messaging, audience and creative direction. Decisions made on purpose, before anything gets designed.",
  },
  {
    icon: Rocket,
    title: "Brand Launch",
    copy: "Website, photography, video, social assets and launch campaigns — everything a new brand needs to enter the world loud.",
  },
  {
    icon: RefreshCw,
    title: "Rebranding",
    copy: "Modernizing businesses with outdated or inconsistent identities — without losing the recognition they've already earned.",
  },
];

export default function Branding() {
  useDocTitle("Branding");
  return (
    <div data-testid="branding-page">
      <PageHero
        eyebrow="Branding & Creative"
        lines={[<>Your brand should look</>, <span key="g" className="text-gold-dark">as good as your business is.</span>]}
        sub="Need a brand? We can build it. Already have one? We'll help people notice it. From a name on a napkin to a complete identity — strategy, logo, guidelines, and launch assets. And for established businesses: rebrands that modernize without starting over."
      >
        <div className="mt-9 flex flex-wrap gap-4">
          <GoldLink to="/start-a-project" testId="branding-hero-cta">Start My Brand</GoldLink>
          <GhostLink to="/work" testId="branding-hero-work">See Brand Work</GhostLink>
        </div>
      </PageHero>

      <section className="border-y border-ink/10 bg-cream">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-28 lg:px-16">
          <SectionHeading
            eyebrow="How Brands Get Built"
            title={<>Start with an idea. <span className="text-gold-dark">We'll build the rest.</span></>}
            sub="Come to us with a full company or a single sentence. This is the path — click through it."
          />
          <Reveal delay={0.1}>
            <div className="mt-12">
              <BrandFlow />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-28 lg:px-16">
        <SectionHeading
          eyebrow="What Branding Covers"
          title={<>The whole identity. <span className="text-gold-dark">Not just the logo.</span></>}
        />
        <div className="mt-14 grid gap-6 sm:grid-cols-2">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.07}>
              <div
                data-testid={`branding-pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="group h-full border border-ink/10 bg-white p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-gold/50 hover:shadow-xl sm:p-10"
              >
                <p.icon size={26} className="text-gold-dark" />
                <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-ink">{p.title}</h3>
                <p className="mt-3.5 text-sm leading-relaxed text-smoke">{p.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-night text-sand">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-28 lg:px-16">
          <SectionHeading
            dark
            eyebrow="Two Starting Points"
            title={<>Wherever you're starting, <span className="text-gold">that's fine.</span></>}
          />
          <div className="mt-14 grid gap-6 lg:grid-cols-2">
            <Reveal>
              <div className="h-full border border-sand/10 p-8 transition-colors duration-500 hover:bg-sand/5 sm:p-10">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Starting from zero</p>
                <h3 className="mt-4 font-serif text-3xl italic">"All I have is an idea."</h3>
                <p className="mt-4 text-sm leading-relaxed text-sand/65">
                  Perfect. We help develop the name and identity, design the logo and visual brand,
                  build the website, shoot the launch commercial, and create the photography and
                  social content — then keep producing monthly while you grow.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full border border-sand/10 p-8 transition-colors duration-500 hover:bg-sand/5 sm:p-10">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Ready for a rebrand</p>
                <h3 className="mt-4 font-serif text-3xl italic">"We've outgrown our look."</h3>
                <p className="mt-4 text-sm leading-relaxed text-sand/65">
                  Also perfect. We audit what's working, keep the equity you've earned, and rebuild
                  the rest — identity, website, content, and a relaunch plan that brings your
                  existing customers along.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-28 lg:px-16">
        <div className="grid gap-4 sm:grid-cols-3">
          <Reveal>
            <MediaImg src={IMAGES.branding} alt="Brand design materials" className="h-72 w-full object-cover" />
          </Reveal>
          <Reveal delay={0.08}>
            <MediaImg src={IMAGES.brandingStudio} alt="Identity system in progress" className="h-72 w-full object-cover sm:mt-10" />
          </Reveal>
          <Reveal delay={0.16}>
            <MediaImg src={IMAGES.meeting} alt="Creative strategy session" className="h-72 w-full object-cover" />
          </Reveal>
        </div>
      </section>

      <CTABanner
        title="From logo to launch."
        sub="One inquiry covers the whole thing — identity, website, content, and the plan to put it in front of people."
      />
    </div>
  );
}
